'use client'

/**
 * Gedeelde terugknop voor portal, backoffice én klant — één bron van waarheid
 * voor plaatsing en gedrag (spec consistente-terugknop).
 *
 * Gedrag:
 *  - Is er in-app history → `router.back()` (gebruiker keert terug waar hij vandaan kwam).
 *  - Geen in-app history (deep-link / PWA koude start) → `router.push(fallback)`.
 *    Fallback = expliciete `fallbackHref` als die er is, anders de afgeleide
 *    parent-route (`parentRoute(pathname)`).
 *
 * History-detectie: de App Router schrijft zelf geen volgnummer in
 * `window.history.state` (de `idx` was een Pages Router-ding), dus stempelt de
 * `HistoryIndexTracker` in de root-layout er een op (`lib/history-index.ts`).
 * Bij de eerste entry van een sessie is die 0 (of afwezig), dus `> 0` betekent
 * "er valt iets terug te gaan".
 *
 * Tap-target ≥ 44px (CLAUDE.md § Portal mobile UI). Op smalle schermen toont
 * 'ie standaard alleen de chevron; vanaf `sm:` het label erbij. `showLabel`
 * forceert het label altijd (bv. desktop-backoffice).
 */

import { useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { ChevronLeft } from 'lucide-react'
import { cn } from '../lib/utils'
import { parentRoute } from '../lib/parent-route'
import { readHistoryIndex } from '../lib/history-index'

export interface BackButtonProps {
  /** Expliciete fallback-bestemming bij ontbrekende history. Default: parent-route. */
  fallbackHref?: string
  /** Zichtbaar label naast de chevron. Default: "Terug". */
  label?: string
  /** Forceer het label altijd zichtbaar (anders alleen vanaf `sm:`). */
  showLabel?: boolean
  className?: string
}

function canGoBack(): boolean {
  if (typeof window === 'undefined') return false
  return readHistoryIndex(window.history.state) > 0
}

export function BackButton({
  fallbackHref,
  label = 'Terug',
  showLabel = false,
  className,
}: BackButtonProps) {
  const router = useRouter()
  const pathname = usePathname()

  const onClick = useCallback(() => {
    if (canGoBack()) {
      router.back()
      return
    }
    router.push(fallbackHref ?? parentRoute(pathname))
  }, [router, pathname, fallbackHref])

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-md text-sm font-medium transition-colors',
        className,
      )}
    >
      <ChevronLeft className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className={cn(showLabel ? 'inline' : 'hidden sm:inline')}>{label}</span>
    </button>
  )
}
